<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import { useRevendaStore } from '@/stores/revenda'
import { useNotificationStore } from '@/stores/notification'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  open: {
    type: Boolean,
    default: false
  },
  revenda: {
    type: Object,
    required: true
  }
})

// Accept both prop styles for better compatibility
const isOpen = computed(() => props.open || props.show);

const emit = defineEmits(['update:show', 'update:open', 'desativado'])

// Use stores
const revendaStore = useRevendaStore()
const notificationStore = useNotificationStore()

// Local state
const loading = ref(false)

// Add this function to make sure we register our modal as open
const registerModalOpen = () => {
  if (window.pedidoModalOpenCount === undefined) {
    window.pedidoModalOpenCount = 0;
  }
  window.pedidoModalOpenCount++;
};

// And this function to unregister it when closed
const unregisterModalOpen = () => {
  if (window.pedidoModalOpenCount) {
    window.pedidoModalOpenCount--;
  }
};

// Watch for modal opening/closing to register with the global tracker
watch(() => isOpen.value, (newValue) => {
  if (newValue) {
    registerModalOpen();
  } else {
    unregisterModalOpen();
  }
}, { immediate: true });

// Also register/unregister on mount/unmount for safety
onMounted(() => {
  if (isOpen.value) {
    registerModalOpen();
  }
});

onUnmounted(() => {
  if (isOpen.value) {
    unregisterModalOpen();
  }
});

const handleDesativar = async (tempo_recarga) => {
  // Extra check to ensure revenda and id exist
  if (!props.revenda?.id) {
    notificationStore.showNotification('Erro: ID da revenda não encontrado', 'error');
    return;
  }
  
  loading.value = true;
  
  try {
    const response = await revendaStore.desativarRevenda(
      props.revenda.id,
      tempo_recarga
    );
    
    if (response.success) {
      notificationStore.showNotification(
        response.message || 'Revenda desativada com sucesso',
        'success'
      )
      
      // Update parent component data
      emit('desativado', { 
        ...props.revenda,
        ativo: 0
      })
      
      // Close the modal
      closeModal();
    } else {
      notificationStore.showNotification(
        response.message || 'Erro ao desativar revenda',
        'error'
      )
    }
  } catch (error) {
    console.error('Error in desativar revenda:', error)
    notificationStore.showNotification(
      'Ocorreu um erro ao processar esta operação',
      'error'
    )
  } finally {
    loading.value = false;
  }
}

const closeModal = () => {
  emit('update:open', false);
  emit('update:show', false);
  unregisterModalOpen();
};
</script>

<template>
  <v-dialog
    :model-value="isOpen" 
    @update:model-value="(val) => { emit('update:open', val); emit('update:show', val); }"
    max-width="500px"
    persistent
    content-class="transparent-dialog"
  >
    <v-card>
      <v-card-title class="text-h5 bg-error text-white">
        <v-icon start icon="mdi-alert-circle" class="mr-2"></v-icon>
        Desativar Revenda
      </v-card-title>
      
      <v-card-text class="pt-4 pb-0">
        <!-- Add a safety check for revenda -->
        <p class="text-body-1 mb-4" v-if="revenda && revenda.nomefantasia">
          Por quanto tempo deseja desativar a revenda
          <strong>{{ revenda.nomefantasia }}</strong>?
        </p>
        <p class="text-body-1 mb-4" v-else>
          Por quanto tempo deseja desativar esta revenda?
        </p>
        
        <v-list>
          <v-list-item 
            prepend-icon="mdi-clock-time-one-outline" 
            title="Desativar por 1 hora"
            @click="handleDesativar('1hora')"
            class="deactivate-option"
            :disabled="loading"
          ></v-list-item>
          
          <v-list-item 
            prepend-icon="mdi-clock-time-two-outline" 
            title="Desativar por 2 horas"
            @click="handleDesativar('2hora')"
            class="deactivate-option"
            :disabled="loading"
          ></v-list-item>
          
          <v-list-item 
            prepend-icon="mdi-calendar-today" 
            title="Desativar até amanhã"
            @click="handleDesativar('ateAmanha')"
            class="deactivate-option"
            :disabled="loading"
          ></v-list-item>
          
          <v-list-item 
            prepend-icon="mdi-lock-outline" 
            title="Desativar para sempre"
            @click="handleDesativar('paraSempre')"
            class="deactivate-option mb-0"
            :disabled="loading"
          ></v-list-item>
        </v-list>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn 
          color="grey-darken-1" 
          variant="text" 
          @click="closeModal"
          :disabled="loading"
        >
          Cancelar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.deactivate-option {
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 4px;
  margin-bottom: 4px;
}

.deactivate-option:hover {
  background-color: rgba(var(--v-theme-error), 0.1);
}

:deep(.v-overlay__scrim) {
  background-color: transparent !important;
}

:deep(.v-overlay__content) {
  box-shadow: none !important;
}

/* Make the dialog card stand out more on the transparent background */
:deep(.v-card) {
  will-change: transform;
  border: solid 1px rgba(0, 0, 0, 0.2) !important;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15) !important;
  transform: translateZ(0);
}
</style>
